import { TOOLTIP_STYLE } from "@/lib/chartTheme";
import { useMemo, useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useVendorPO } from "@/data/VendorPOContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RoleContextBanner } from "@/components/RoleContextBanner";
import { RoleHandoffInbox } from "@/components/RoleHandoffInbox";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { useUISkin } from "@/data/UISkinContext";
import { CockpitHeader, StatStrip, type StatItem } from "@/components/dashboard/primitives";
import {
   DollarSign, TrendingUp, AlertTriangle, ArrowRight, Clock, Wallet,
   CreditCard, FileText, ShieldAlert, Receipt, Banknote, Scale,
   CircleDollarSign, BadgeDollarSign, Percent, ChevronRight, Zap,
   CheckCircle, XCircle, BarChart3, Truck,
} from "lucide-react";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";
import { resolveSupplierName } from "@/data/warehouseExecution";

const TODAY = new Date();
const DAY_MS = 86_400_000;

const CHART_COLORS = {
  primary: "hsl(var(--chart-1))",
  success: "hsl(var(--chart-2))",
  warning: "hsl(var(--chart-5))",
  destructive: "hsl(var(--destructive))",
  muted: "hsl(var(--muted-foreground))",
};

function fmt$(v: number) {
  return `$${v.toLocaleString()}`;
}

export function CFOCommandCenter() {
   const { jobs, issues, credits, quotes, contractors, opportunities, purchaseOrders } = useAppData();
   const { vendors } = useVendorPO();
   const { skin } = useUISkin();
   const isCockpit = skin === "cockpit";

  // ── Cash Flow ──
  const cashFlow = useMemo(() => {
    const allJobs = jobs;
    const completedJobs = allJobs.filter((j) => j.currentStage === "Completed");
    const openJobs = allJobs.filter((j) => j.currentStage !== "Completed");

    const totalCollected = completedJobs
      .filter((j) => j.financials?.paymentStatus === "Paid")
      .reduce((s, j) => s + getJobDisplayValue(j), 0);

    const partialCollected = allJobs
      .filter((j) => j.financials?.paymentStatus === "Partial Payment")
      .reduce((s, j) => s + Math.round(getJobDisplayValue(j) * 0.5), 0);

    const totalCashIn = totalCollected + partialCollected;

    const depositsExpected = openJobs.filter((j) => j.financials).length;
    const depositsCollected = openJobs.filter(
      (j) => j.financials && j.financials.paymentStatus !== "Not Invoiced"
    ).length;

    const outstandingReceivables = allJobs
      .filter((j) => j.financials && ["Invoiced", "Partial Payment", "Overdue"].includes(j.financials.paymentStatus))
      .reduce((s, j) => s + getJobDisplayValue(j), 0);

    const upcomingInstalls = openJobs.filter((j) => {
      if (!j.scheduledInstallDate) return false;
      const d = (new Date(j.scheduledInstallDate).getTime() - TODAY.getTime()) / DAY_MS;
      return d >= 0 && d <= 14;
    });
    const upcomingRevenue = upcomingInstalls.reduce((s, j) => s + getJobDisplayValue(j), 0);

    const overdueJobs = allJobs.filter((j) => j.financials?.paymentStatus === "Overdue");
    const overdueValue = overdueJobs.reduce((s, j) => s + getJobDisplayValue(j), 0);

    const completedNotPaid = completedJobs.filter((j) => j.financials && j.financials.paymentStatus !== "Paid");
    const completedNotPaidValue = completedNotPaid.reduce((s, j) => s + getJobDisplayValue(j), 0);

    return {
      totalCashIn, depositsExpected, depositsCollected, outstandingReceivables,
      upcomingRevenue, upcomingCount: upcomingInstalls.length,
      overdueCount: overdueJobs.length, overdueValue,
      completedNotPaid: completedNotPaid.length, completedNotPaidValue,
    };
  }, [jobs]);

  // ── Accounts Receivable ──
  const arBuckets = useMemo(() => {
    const arJobs = jobs.filter(
      (j) => j.financials && ["Invoiced", "Partial Payment", "Overdue"].includes(j.financials.paymentStatus)
    );

    const buckets = { "0–7": [] as typeof arJobs, "8–15": [] as typeof arJobs, "16–30": [] as typeof arJobs, "30+": [] as typeof arJobs };

    arJobs.forEach((j) => {
      // Estimate aging from scheduled install or activity
      const refDate = j.scheduledInstallDate ? new Date(j.scheduledInstallDate) : (j.activityHistory.length > 0 ? new Date(j.activityHistory[j.activityHistory.length - 1].date) : TODAY);
      const ageDays = Math.max(0, (TODAY.getTime() - refDate.getTime()) / DAY_MS);
      if (ageDays <= 7) buckets["0–7"].push(j);
      else if (ageDays <= 15) buckets["8–15"].push(j);
      else if (ageDays <= 30) buckets["16–30"].push(j);
      else buckets["30+"].push(j);
    });

    return buckets;
  }, [jobs]);

  // ── Job Financial Summary ──
  const jobFinancials = useMemo(() => {
    return jobs.map((j) => {
      const contractValue = getJobDisplayValue(j);
      const changeOrderTotal = (j.changeOrders || [])
        .filter((co) => co.status === "Signed")
        .reduce((s, co) => s + co.changeAmount, 0);
      const totalCredits = credits.filter((c) => c.jobId === j.id).reduce((s, c) => s + c.creditAmount, 0);
      const currentValue = contractValue + changeOrderTotal - totalCredits;
      const collected = j.financials?.paymentStatus === "Paid" ? currentValue
        : j.financials?.paymentStatus === "Partial Payment" ? Math.round(currentValue * 0.5) : 0;
      const variance = contractValue > 0 ? Math.round(((currentValue - contractValue) / contractValue) * 100) : 0;

      return {
        job: j, contractValue, changeOrderTotal, totalCredits,
        currentValue, collected, remaining: currentValue - collected, variance,
      };
    }).sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));
  }, [jobs, credits]);

  // ── Margin & Pricing ──
  const margin = useMemo(() => {
    const belowTarget = quotes.filter((q) => q.pricingStatus === "Below Target");
    const belowFloor = quotes.filter((q) => q.pricingStatus === "Below Floor");
    const withOverrides = quotes.filter((q) => q.pricingOverride);
    const totalOverrideImpact = withOverrides.reduce((s, q) => s + (q.pricingOverride?.adjustedPrice ?? 0) - (q.pricingOverride?.originalCalculatedPrice ?? 0), 0);

    const overridesByRep = new Map<string, number>();
    withOverrides.forEach((q) => {
      const rep = q.createdBy || "Unknown";
      overridesByRep.set(rep, (overridesByRep.get(rep) || 0) + 1);
    });

    const totalJobMargin = jobs.reduce((s, j) => s + (j.financials?.marginEstimate ?? 0), 0);
    const totalJobRevenue = jobs.reduce((s, j) => s + getJobDisplayValue(j), 0);
    const marginPct = totalJobRevenue > 0 ? Math.round((totalJobMargin / totalJobRevenue) * 100) : 0;

    return { belowTarget: belowTarget.length, belowFloor: belowFloor.length, overrides: withOverrides.length, totalOverrideImpact, overridesByRep, marginPct, totalJobMargin };
  }, [quotes, jobs]);

  // ── Credits ──
  const creditMetrics = useMemo(() => {
    const total = credits.reduce((s, c) => s + c.creditAmount, 0);
    const byType = new Map<string, { count: number; amount: number }>();
    credits.forEach((c) => {
      const e = byType.get(c.creditType) || { count: 0, amount: 0 };
      e.count++;
      e.amount += c.creditAmount;
      byType.set(c.creditType, e);
    });

    const byContractor = new Map<string, { count: number; amount: number; name: string }>();
    credits.filter((c) => c.relatedContractorId).forEach((c) => {
      const e = byContractor.get(c.relatedContractorId!) || { count: 0, amount: 0, name: c.relatedCrewName || "Unknown" };
      e.count++;
      e.amount += c.creditAmount;
      byContractor.set(c.relatedContractorId!, e);
    });
    const topOffenders = [...byContractor.entries()]
      .map(([id, d]) => ({ id, ...d }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);

    return { total, count: credits.length, byType: [...byType.entries()], topOffenders };
  }, [credits]);

  // ── Change Orders ──
  const changeOrderMetrics = useMemo(() => {
    const allCOs = jobs.flatMap((j) => (j.changeOrders || []).map((co) => ({ ...co, jobId: j.id, customerName: j.customerName })));
    const totalValue = allCOs
      .filter((co) => co.status === "Signed")
      .reduce((s, co) => s + co.changeAmount, 0);
    const unsigned = allCOs.filter((co) => co.status === "Draft" || co.status === "Pending Review");
    return { total: allCOs.length, totalValue, unsigned: unsigned.length, items: allCOs };
  }, [jobs]);

  // ── Deposit Compliance ──
  const depositIssues = useMemo(() => {
    return jobs.filter((j) => {
      const hasContract = j.contractDocument?.status === "Signed";
      if (!hasContract) return false;
      return !j.financials || j.financials.paymentStatus === "Not Invoiced";
    });
  }, [jobs]);

  // ── Financial Risk Items ──
  const riskItems = useMemo(() => {
    type RiskItem = { id: string; title: string; reason: string; severity: "critical" | "warning"; value: number; link: string; entity: string };
    const items: RiskItem[] = [];

    // Unpaid high-value completed jobs
    jobs.filter((j) => j.currentStage === "Completed" && j.financials && j.financials.paymentStatus !== "Paid").forEach((j) => {
      const val = getJobDisplayValue(j);
        items.push({ id: `risk-unpaid-${j.id}`, title: `${j.jobNumber || j.id} — ${j.customerName}`, reason: "Completed but not paid", severity: val > 10000 ? "critical" : "warning", value: val, link: `/jobs/${j.id}`, entity: "Job" });
    });

    // Excessive credits per job
    jobs.forEach((j) => {
      const jobCredits = credits.filter((c) => c.jobId === j.id);
      const totalCr = jobCredits.reduce((s, c) => s + c.creditAmount, 0);
      if (totalCr > 500) {
        items.push({ id: `risk-credit-${j.id}`, title: `${j.jobNumber || j.id} — ${j.customerName}`, reason: `${fmt$(totalCr)} in credits`, severity: totalCr > 1000 ? "critical" : "warning", value: totalCr, link: `/jobs/${j.id}`, entity: "Job" });
      }
    });

    // Overdue payments
    jobs.filter((j) => j.financials?.paymentStatus === "Overdue").forEach((j) => {
        items.push({ id: `risk-overdue-${j.id}`, title: `${j.jobNumber || j.id} — ${j.customerName}`, reason: "Payment overdue", severity: "critical", value: getJobDisplayValue(j), link: `/jobs/${j.id}`, entity: "Job" });
    });

    // Below floor quotes
    quotes.filter((q) => q.pricingStatus === "Below Floor").forEach((q) => {
      items.push({ id: `risk-floor-${q.id}`, title: `${q.id} — ${q.customerName}`, reason: "Priced below floor", severity: "critical", value: q.totalEstimate || 0, link: `/opportunities/${q.id}`, entity: "Quote" });
    });

    return items.sort((a, b) => (a.severity === "critical" ? -1 : 1) - (b.severity === "critical" ? -1 : 1) || b.value - a.value);
  }, [jobs, credits, quotes]);

  // ── Chart Data ──
  const arChartData = Object.entries(arBuckets).map(([bucket, jbs]) => ({
    name: bucket,
    value: jbs.reduce((s, j) => s + getJobDisplayValue(j), 0),
    count: jbs.length,
  }));

  const creditChartData = creditMetrics.byType.map(([type, d]) => ({
    name: type.replace(" Credit", ""),
    amount: d.amount,
  }));

  const cockpitCfoKpis: StatItem[] = [
    { label: "Cash Collected", value: fmt$(cashFlow.totalCashIn), accent: "good" },
    { label: "Outstanding AR", value: fmt$(cashFlow.outstandingReceivables), accent: cashFlow.outstandingReceivables > 0 ? "warn" : "good" },
    { label: "Overdue", value: `${cashFlow.overdueCount} jobs`, accent: cashFlow.overdueCount > 0 ? "bad" : "good" },
    { label: "Upcoming Revenue", value: fmt$(cashFlow.upcomingRevenue) },
    { label: "Margin", value: `${margin.marginPct}%`, accent: margin.marginPct >= 30 ? "good" : "warn" },
    { label: "Total Credits", value: fmt$(creditMetrics.total), accent: creditMetrics.total > 0 ? "bad" : "good" },
  ];

  return (
    <div className={isCockpit ? "space-y-6 max-w-[1600px] mx-auto w-full" : "space-y-5"}>
      <RoleContextBanner />
      <RoleHandoffInbox />

      {/* ═══ HEADER ═══ */}
      {isCockpit ? (
        <CockpitHeader
          title="CFO Command Center"
          subtitle={`Financial oversight · cash flow · risk detection · ${format(TODAY, "EEEE, MMMM d, yyyy")}`}
          right={
            <Link to="/accounting" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5 px-2.5 py-1.5">
              Full Accounting <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          }
        />
      ) : (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-lg font-bold tracking-tight">CFO Command Center</h1>
            <p className="text-xs text-muted-foreground">
              Financial Oversight · Cash Flow · Risk Detection · {format(TODAY, "EEEE, MMMM d, yyyy")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/accounting" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              Full Accounting <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}

      {/* ═══ FINANCIAL RISK ALERTS (TOP) ═══ */}
      {riskItems.length > 0 && (
        <Card className="border-destructive/30 bg-destructive/[0.02] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-destructive/10 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-destructive" />
                </div>
                <div>
                  <h2 className="text-sm font-bold tracking-tight">Financial Risks Requiring Action</h2>
                  <p className="text-[10px] text-muted-foreground">
                    {riskItems.filter((i) => i.severity === "critical").length} critical · {riskItems.filter((i) => i.severity === "warning").length} warnings · {fmt$(riskItems.reduce((s, i) => s + i.value, 0))} at risk
                  </p>
                </div>
              </div>
              <Link to="/alerts-history" className="text-[10px] text-primary hover:underline inline-flex items-center gap-1">
                All Alerts <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {riskItems.slice(0, 6).map((item) => (
                <Link key={item.id} to={item.link}
                  className={`group flex items-start gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/40 ${
                    item.severity === "critical" ? "border-destructive/25 bg-destructive/[0.03]" : "border-warning/25 bg-warning/[0.03]"
                  }`}>
                  <div className={`mt-0.5 h-2 w-2 rounded-full shrink-0 ${item.severity === "critical" ? "bg-destructive" : "bg-warning"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-semibold truncate">{item.title}</span>
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                        item.severity === "critical" ? "bg-destructive/10 text-destructive" : "bg-warning/10 text-warning"
                      }`}>{item.severity}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">{item.reason} · {fmt$(item.value)}</p>
                  </div>
                  <ArrowRight className="h-3 w-3 text-muted-foreground/40 group-hover:text-primary shrink-0 mt-1 transition-colors" />
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ═══ TOP KPI STRIP ═══ */}
      {isCockpit ? (
        <StatStrip items={cockpitCfoKpis} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {[
            { label: "Cash Collected", value: fmt$(cashFlow.totalCashIn), icon: Banknote, color: "text-success" },
            { label: "Outstanding AR", value: fmt$(cashFlow.outstandingReceivables), icon: Receipt, color: cashFlow.outstandingReceivables > 0 ? "text-warning" : "text-success" },
            { label: "Overdue", value: `${cashFlow.overdueCount} jobs`, icon: AlertTriangle, color: cashFlow.overdueCount > 0 ? "text-destructive" : "text-success" },
            { label: "Upcoming Revenue", value: fmt$(cashFlow.upcomingRevenue), icon: TrendingUp, color: "text-primary" },
            { label: "Margin", value: `${margin.marginPct}%`, icon: Percent, color: margin.marginPct >= 30 ? "text-success" : "text-warning" },
            { label: "Total Credits", value: fmt$(creditMetrics.total), icon: CreditCard, color: creditMetrics.total > 0 ? "text-destructive" : "text-success" },
          ].map((s) => (
            <Card key={s.label} className="shadow-none border h-full">
              <CardContent className="p-3 text-center">
                <s.icon className={`h-4 w-4 mx-auto mb-1 ${s.color}`} />
                <p className={`text-xl font-bold leading-tight ${s.color}`}>{s.value}</p>
                <p className="text-[9px] text-muted-foreground font-medium mt-0.5 uppercase tracking-wider">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ═══ TABBED SECTIONS ═══ */}
      <Tabs defaultValue="cashflow">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="cashflow" className="text-xs gap-1"><Wallet className="h-3 w-3" /> Cash Flow</TabsTrigger>
          <TabsTrigger value="ar" className="text-xs gap-1"><Receipt className="h-3 w-3" /> AR</TabsTrigger>
          <TabsTrigger value="jobs" className="text-xs gap-1"><FileText className="h-3 w-3" /> Job Financials</TabsTrigger>
          <TabsTrigger value="margin" className="text-xs gap-1"><Scale className="h-3 w-3" /> Margin</TabsTrigger>
          <TabsTrigger value="credits" className="text-xs gap-1"><CreditCard className="h-3 w-3" /> Credits</TabsTrigger>
           <TabsTrigger value="changeorders" className="text-xs gap-1"><FileText className="h-3 w-3" /> Change Orders</TabsTrigger>
           <TabsTrigger value="purchaseorders" className="text-xs gap-1"><Truck className="h-3 w-3" /> Purchase Orders</TabsTrigger>
           <TabsTrigger value="deposits" className="text-xs gap-1"><BadgeDollarSign className="h-3 w-3" /> Deposits</TabsTrigger>
        </TabsList>

        {/* ──── CASH FLOW ──── */}
        <TabsContent value="cashflow" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Banknote className="h-4 w-4 text-success" /> Cash Position
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3">
                {[
                  { label: "Total Collected", value: fmt$(cashFlow.totalCashIn), color: "text-success" },
                  { label: "Outstanding Receivables", value: fmt$(cashFlow.outstandingReceivables), color: "text-warning" },
                  { label: "Overdue Payments", value: `${cashFlow.overdueCount} jobs · ${fmt$(cashFlow.overdueValue)}`, color: "text-destructive" },
                  { label: "Deposits", value: `${cashFlow.depositsCollected} of ${cashFlow.depositsExpected} collected`, color: "text-primary" },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between border-b border-border/50 pb-2 last:border-0">
                    <span className="text-xs text-muted-foreground">{row.label}</span>
                    <span className={`text-sm font-bold ${row.color}`}>{row.value}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-primary" /> Revenue Pipeline
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3">
                {[
                  { label: "Upcoming Installs (14d)", value: `${cashFlow.upcomingCount} jobs · ${fmt$(cashFlow.upcomingRevenue)}` },
                  { label: "Completed Unpaid", value: `${cashFlow.completedNotPaid} jobs · ${fmt$(cashFlow.completedNotPaidValue)}` },
                  { label: "Total Margin", value: fmt$(margin.totalJobMargin) },
                  { label: "Margin %", value: `${margin.marginPct}%` },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between border-b border-border/50 pb-2 last:border-0">
                    <span className="text-xs text-muted-foreground">{row.label}</span>
                    <span className="text-sm font-bold">{row.value}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ──── AR ──── */}
        <TabsContent value="ar" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-warning" /> Aging Buckets
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={arChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v: number) => fmt$(v)} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {arChartData.map((_, i) => (
                          <Cell key={i} fill={i < 2 ? CHART_COLORS.warning : CHART_COLORS.destructive} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {Object.entries(arBuckets).map(([bucket, jbs]) => (
                    <div key={bucket} className="text-center p-2 rounded-lg bg-muted/30 border">
                      <p className="text-lg font-bold">{jbs.length}</p>
                      <p className="text-[9px] text-muted-foreground font-medium uppercase">{bucket} days</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Receipt className="h-4 w-4 text-primary" /> Open Receivables
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {jobs
                    .filter((j) => j.financials && ["Invoiced", "Partial Payment", "Overdue"].includes(j.financials.paymentStatus))
                    .sort((a, b) => (getJobDisplayValue(b)) - (getJobDisplayValue(a)))
                    .slice(0, 12)
                    .map((j) => (
                      <Link key={j.id} to={`/jobs/${j.id}`}
                        className="flex items-center justify-between p-2 rounded-md hover:bg-muted/40 transition-colors border-b border-border/30 last:border-0">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold truncate">{j.customerName}</p>
                          <p className="text-[10px] text-muted-foreground">{j.jobNumber || j.id}</p>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                          <p className="text-xs font-bold">{fmt$(getJobDisplayValue(j))}</p>
                          <Badge variant={j.financials?.paymentStatus === "Overdue" ? "destructive" : "secondary"} className="text-[9px]">
                            {j.financials?.paymentStatus}
                          </Badge>
                        </div>
                      </Link>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ──── JOB FINANCIALS ──── */}
        <TabsContent value="jobs" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-2 px-4 pt-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" /> Job Financial Summary
                </CardTitle>
                <Link to="/jobs" className="text-[10px] text-primary hover:underline inline-flex items-center gap-1">
                  All Jobs <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 font-semibold text-muted-foreground">Job</th>
                      <th className="pb-2 font-semibold text-muted-foreground text-right">Contract</th>
                      <th className="pb-2 font-semibold text-muted-foreground text-right">COs</th>
                      <th className="pb-2 font-semibold text-muted-foreground text-right">Credits</th>
                      <th className="pb-2 font-semibold text-muted-foreground text-right">Current</th>
                      <th className="pb-2 font-semibold text-muted-foreground text-right">Collected</th>
                      <th className="pb-2 font-semibold text-muted-foreground text-right">Var%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobFinancials.slice(0, 15).map((jf) => (
                      <tr key={jf.job.id} className="border-b border-border/30 hover:bg-muted/20">
                        <td className="py-2">
                          <Link to={`/jobs/${jf.job.id}`} className="text-primary hover:underline font-medium">
                            {jf.job.id}
                          </Link>
                          <span className="text-muted-foreground ml-1">{jf.job.customerName}</span>
                        </td>
                        <td className="py-2 text-right font-medium">{fmt$(jf.contractValue)}</td>
                        <td className="py-2 text-right">{jf.changeOrderTotal !== 0 ? fmt$(jf.changeOrderTotal) : "—"}</td>
                        <td className="py-2 text-right text-destructive">{jf.totalCredits > 0 ? `-${fmt$(jf.totalCredits)}` : "—"}</td>
                        <td className="py-2 text-right font-bold">{fmt$(jf.currentValue)}</td>
                        <td className="py-2 text-right">{fmt$(jf.collected)}</td>
                        <td className={`py-2 text-right font-medium ${Math.abs(jf.variance) > 10 ? "text-destructive" : "text-muted-foreground"}`}>
                          {jf.variance !== 0 ? `${jf.variance > 0 ? "+" : ""}${jf.variance}%` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ──── MARGIN ──── */}
        <TabsContent value="margin" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Scale className="h-4 w-4 text-primary" /> Margin & Pricing Health
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Overall Margin", value: `${margin.marginPct}%`, color: margin.marginPct >= 30 ? "text-success" : "text-destructive", icon: Percent },
                    { label: "Below Target", value: margin.belowTarget, color: margin.belowTarget > 0 ? "text-warning" : "text-success", icon: AlertTriangle },
                    { label: "Below Floor", value: margin.belowFloor, color: margin.belowFloor > 0 ? "text-destructive" : "text-success", icon: XCircle },
                    { label: "Overrides Used", value: margin.overrides, color: margin.overrides > 3 ? "text-warning" : "text-muted-foreground", icon: ShieldAlert },
                  ].map((m) => (
                    <div key={m.label} className="p-3 rounded-lg border bg-muted/20 text-center">
                      <m.icon className={`h-4 w-4 mx-auto mb-1 ${m.color}`} />
                      <p className={`text-xl font-bold ${m.color}`}>{m.value}</p>
                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">{m.label}</p>
                    </div>
                  ))}
                </div>
                {margin.totalOverrideImpact !== 0 && (
                  <div className="p-3 rounded-lg border border-warning/30 bg-warning/5">
                    <p className="text-xs text-muted-foreground">Total Override Impact</p>
                    <p className="text-sm font-bold text-warning">{fmt$(Math.abs(margin.totalOverrideImpact))} {margin.totalOverrideImpact < 0 ? "discount" : "premium"}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-warning" /> Override Usage by Rep
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {margin.overridesByRep.size > 0 ? (
                  <div className="space-y-2">
                    {[...margin.overridesByRep.entries()].sort((a, b) => b[1] - a[1]).map(([rep, count]) => (
                      <div key={rep} className="flex items-center justify-between p-2 rounded-md bg-muted/20 border">
                        <span className="text-xs font-medium">{rep}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant={count > 3 ? "destructive" : "secondary"} className="text-[9px]">
                            {count} override{count !== 1 ? "s" : ""}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground py-4 text-center">No pricing overrides recorded</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ──── CREDITS ──── */}
        <TabsContent value="credits" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-destructive" /> Credit Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="flex items-center gap-4 mb-4 p-3 rounded-lg bg-destructive/5 border border-destructive/15">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Total Credits</p>
                    <p className="text-2xl font-bold text-destructive">{fmt$(creditMetrics.total)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Count</p>
                    <p className="text-2xl font-bold">{creditMetrics.count}</p>
                  </div>
                </div>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={creditChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={80} />
                      <Tooltip formatter={(v: number) => fmt$(v)} />
                      <Bar dataKey="amount" fill={CHART_COLORS.destructive} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-warning" /> Top Credit Offenders
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {creditMetrics.topOffenders.length > 0 ? (
                  <div className="space-y-2">
                    {creditMetrics.topOffenders.map((o) => (
                      <Link key={o.id} to={`/contractors/${o.id}`}
                        className="flex items-center justify-between p-2.5 rounded-md border hover:bg-muted/40 transition-colors">
                        <div>
                          <p className="text-xs font-semibold">{o.name}</p>
                          <p className="text-[10px] text-muted-foreground">{o.count} credit{o.count !== 1 ? "s" : ""}</p>
                        </div>
                        <span className="text-sm font-bold text-destructive">{fmt$(o.amount)}</span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground py-4 text-center">No contractor-attributed credits</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ──── CHANGE ORDERS ──── */}
        <TabsContent value="changeorders" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" /> Change Order Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="p-3 rounded-lg border text-center bg-muted/20">
                  <p className="text-xl font-bold">{changeOrderMetrics.total}</p>
                  <p className="text-[9px] text-muted-foreground uppercase font-medium">Total COs</p>
                </div>
                <div className="p-3 rounded-lg border text-center bg-muted/20">
                  <p className="text-xl font-bold text-success">{fmt$(changeOrderMetrics.totalValue)}</p>
                  <p className="text-[9px] text-muted-foreground uppercase font-medium">Net Impact</p>
                </div>
                <div className="p-3 rounded-lg border text-center bg-muted/20">
                  <p className={`text-xl font-bold ${changeOrderMetrics.unsigned > 0 ? "text-warning" : "text-success"}`}>{changeOrderMetrics.unsigned}</p>
                  <p className="text-[9px] text-muted-foreground uppercase font-medium">Unsigned</p>
                </div>
              </div>
              {changeOrderMetrics.items.length > 0 ? (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {changeOrderMetrics.items.slice(0, 10).map((co) => (
                    <Link key={co.id} to={`/jobs/${co.jobId}`}
                      className="flex items-center justify-between p-2 rounded-md border-b border-border/30 hover:bg-muted/30 transition-colors last:border-0">
                      <div>
                        <span className="text-xs font-medium">{co.jobId}</span>
                        <span className="text-[10px] text-muted-foreground ml-2">{co.customerName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold">{fmt$(co.changeAmount)}</span>
                        <Badge variant={co.status === "Signed" ? "default" : "outline"} className="text-[9px]">{co.status}</Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground py-4 text-center">No change orders recorded</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ──── DEPOSIT COMPLIANCE ──── */}
        <TabsContent value="deposits" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <BadgeDollarSign className="h-4 w-4 text-warning" /> Deposit Compliance
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {depositIssues.length > 0 ? (
                <>
                  <div className="p-3 rounded-lg bg-warning/5 border border-warning/20 mb-4">
                    <p className="text-xs text-muted-foreground">Jobs with signed contracts but no deposit</p>
                    <p className="text-2xl font-bold text-warning">{depositIssues.length}</p>
                  </div>
                  <div className="space-y-1">
                    {depositIssues.slice(0, 10).map((j) => (
                      <Link key={j.id} to={`/jobs/${j.id}`}
                        className="flex items-center justify-between p-2 rounded-md hover:bg-muted/40 transition-colors border-b border-border/30 last:border-0">
                        <div>
                          <p className="text-xs font-semibold">{j.customerName}</p>
                           <p className="text-[10px] text-muted-foreground">{j.jobNumber || j.id} · {j.currentStage}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-bold">{fmt$(getJobDisplayValue(j))}</p>
                          <Badge variant="outline" className="text-[9px] text-warning border-warning/30">No Deposit</Badge>
                        </div>
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <div className="py-8 text-center">
                  <CheckCircle className="h-8 w-8 text-success mx-auto mb-2" />
                  <p className="text-sm font-medium text-success">All deposits compliant</p>
                  <p className="text-[10px] text-muted-foreground">No contracts missing deposits</p>
                </div>
              )}
            </CardContent>
          </Card>
         </TabsContent>

         {/* ──── PURCHASE ORDERS ──── */}
         <TabsContent value="purchaseorders" className="space-y-4 mt-4">
           <Card>
             <CardHeader className="pb-2 px-4 pt-4">
               <CardTitle className="text-sm font-semibold flex items-center gap-2">
                 <Truck className="h-4 w-4 text-primary" /> Purchase Orders
               </CardTitle>
             </CardHeader>
             <CardContent className="px-4 pb-4">
               {purchaseOrders.length > 0 ? (
                 <div className="overflow-x-auto">
                   <table className="w-full text-xs">
                     <thead>
                       <tr className="border-b text-left">
                         <th className="pb-2 font-semibold text-muted-foreground">PO #</th>
                         <th className="pb-2 font-semibold text-muted-foreground">Vendor</th>
                         <th className="pb-2 font-semibold text-muted-foreground text-right">Amount</th>
                         <th className="pb-2 font-semibold text-muted-foreground">Status</th>
                         <th className="pb-2 font-semibold text-muted-foreground">Job</th>
                         <th className="pb-2 font-semibold text-muted-foreground">Date</th>
                       </tr>
                     </thead>
                     <tbody>
{purchaseOrders
                          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                          .slice(0, 20)
                          .map((po) => (
                            <tr key={po.id} className="border-b border-border/30 hover:bg-muted/20">
                              <td className="py-2">
                                <Link to={`/purchase-orders/${po.id}`} className="text-primary hover:underline font-medium">
                                  {po.id}
                                </Link>
                              </td>
                              <td className="py-2">{resolveSupplierName(po, vendors)}</td>
                              <td className="py-2 text-right font-bold">{fmt$(po.totalCost)}</td>
                              <td className="py-2">
                                <Badge variant={
                                  po.status === "Received" ? "secondary" :
                                  po.status === "Partially Received" ? "warning" : "outline"
                                } className="text-[9px]">
                                  {po.status}
                                </Badge>
                              </td>
                              <td className="py-2">
                                <Link to={`/jobs/${po.jobId}`} className="text-primary hover:underline text-[10px]">
                                  {po.jobId}
                                </Link>
                              </td>
                              <td className="py-2 text-[10px] text-muted-foreground">
                                {format(new Date(po.createdAt), "MMM d, yyyy")}
                              </td>
                            </tr>
                          ))}
                     </tbody>
                   </table>
                 </div>
               ) : (
                 <div className="py-8 text-center">
                   <FileText className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                   <p className="text-sm font-medium text-muted-foreground">No purchase orders recorded</p>
                   <p className="text-[10px] text-muted-foreground">Purchase orders will appear here once created</p>
                 </div>
               )}
             </CardContent>
           </Card>
         </TabsContent>
       </Tabs>
    </div>
  );
}
